import React from "react";
import { ScrollArea } from "components/ui/scroll-area";
import { formatDate } from "lib/utils";

interface OrderDetailsProps {
  orderDetails: {
    cn_date: string | null;
    cn_approval_date: string | null;
    booking_branch: string | null;
    actual_weight: number | null;
    charge_weight: number | null;
    items: Array<{ material_name: string; material_desc: string; qty: number }>;
    ewaybill_details: Array<{
      ewaybill_number: string;
      ewaybill_validdate: string | null;
    }>;
    invoice_details: Array<{
      invoice_number: string;
      invoice_date: string | null;
      invoice_value: number | null;
    }>;
  };
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ orderDetails }) => (
  <ScrollArea className="flex-1 min-h-0">
    <div className="mb-6">
      <h4 className="text-sm font-medium text-gray-700 mb-2">CN Details</h4>
      <div className="py-4 px-4 bg-gray-50 rounded-lg space-y-3">
        <div className="flex justify-between text-sm text-gray-700">
          <div>
            <span className="font-medium">CN Date: </span>
            {orderDetails.cn_date ? formatDate(orderDetails.cn_date) : "—"}
          </div>
          <div>
            <span className="font-medium">CN Approval Date: </span>
            {orderDetails.cn_approval_date ? formatDate(orderDetails.cn_approval_date) : "—"}
          </div>
        </div>

        <div className="flex justify-between text-sm text-gray-700">
          <div>
            <span className="font-medium">Actual Weight: </span>
            {orderDetails.actual_weight ? `${orderDetails.actual_weight} kg` : "—"}
          </div>
          <div>
            <span className="font-medium">Charge Weight: </span>
            {orderDetails.charge_weight ? `${orderDetails.charge_weight} kg` : "—"}
          </div>
        </div>

        <div className="text-sm text-gray-700">
          <span className="font-medium">Booking Branch: </span>
          {orderDetails.booking_branch || "—"}
        </div>
      </div>
    </div>

    <div className="mb-6">
      <h4 className="text-sm font-medium text-gray-700 mb-3">Items (as per E-Way Bill)</h4>
      <div className="space-y-2">
        {orderDetails.items.map((item, index) => (
          <div key={index} className="flex justify-between items-center py-3 px-3 bg-gray-50 rounded">
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">{item.material_name}</div>
              <div className="text-xs text-gray-600 mt-1">{item.material_desc}</div>
            </div>
            <span className="text-sm font-medium text-gray-700 ml-4">Qty: {item.qty}</span>
          </div>
        ))}
      </div>
    </div>

    <div className="mb-6 bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-700 mb-3">E-Way Bill Details</h4>

      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {orderDetails.ewaybill_details.map((eway, i) => (
          <div
            key={i}
            className="flex justify-between items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 w-[48%]"
          >
            <span>{eway.ewaybill_number}</span>
            <span className="mx-2">—</span>
            <span>{eway.ewaybill_validdate ? formatDate(eway.ewaybill_validdate) : "—"}</span>
          </div>
        ))}
      </div>
    </div>

    <div className="mb-6 bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-700 mb-3">Invoice Details</h4>

      <div className="space-y-2">
        {orderDetails.invoice_details.map((inv, i) => (
          <div
            key={i}
            className="flex justify-between items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-800 w-[55%]"
          >
            <span>{inv.invoice_number}</span>
            <span>—</span>

            <span>{inv.invoice_date ? formatDate(inv.invoice_date) : "—"}</span>

            <span>—</span>
            <span>{inv.invoice_value !== null ? `₹${inv.invoice_value}` : "—"}</span>
          </div>
        ))}
      </div>
    </div>
  </ScrollArea>
);

export default OrderDetails;
